import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'vp-theme-mode';

/**
 * SP-7b dark mode provider.
 *
 * Strategy: keep Velzon and our custom `--vp-*` tokens aligned on the same
 * data attribute. Velzon SCSS reacts to `data-layout-mode`/`data-sidebar`/
 * `data-topbar`/`data-bs-theme`; our custom tokens (in vp-custom.scss)
 * override the `[data-layout-mode="dark"]` block. localStorage drives
 * persistence; first visit falls back to OS preference.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return 'light';
    const saved = window.localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
    if (saved === 'light' || saved === 'dark') return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute('data-layout-mode', mode);
    html.setAttribute('data-sidebar', mode);
    html.setAttribute('data-topbar', mode);
    html.setAttribute('data-bs-theme', mode);
    window.localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const toggleTheme = () => setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  const setTheme = (next: ThemeMode) => setMode(next);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Returns the theme context, or a light-mode read-only fallback when no
 * ThemeProvider is in the tree (e.g. legacy unit tests that mount components
 * in isolation). The fallback prevents `useTheme must be used within
 * ThemeProvider` crashes from breaking tests that don't care about theming.
 */
export function useTheme(): ThemeContextType {
  const ctx = useContext(ThemeContext);
  if (ctx) return ctx;
  return { mode: 'light', toggleTheme: () => {}, setTheme: () => {} };
}
